import ArticleIcon from '@mui/icons-material/Article';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BoltIcon from '@mui/icons-material/Bolt';
import StarIcon from '@mui/icons-material/Star';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { bool, func, string } from 'prop-types';
import React, { useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import acknowledgeCreateDrawer from 'store/app/ui/drawers/acknowledgeCreate/action';
import formDrawer from 'store/app/ui/drawers/form/action';
import SignIcon from 'assets/icons/sign.svg';
import FileInput from 'components/@home/@messages/ChatPanel/SendForm/controls/AttachFile/FileInput';
import {
  OmmnioSignInactiveTooltipContent,
  richTooltipSlotProps,
} from 'components/@home/@documents/SentDocuments/edit-steps/SetupDocumentPalette';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';

const menuItemSx = theme => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  minHeight: 44,
  padding: theme.spacing(1, 1.5),
});

const iconSx = {
  color: 'text.secondary',
  display: 'inline-flex',
  alignItems: 'center',
};

const signMenuIconSx = {
  width: 20,
  height: 20,
  display: 'block',
  flexShrink: 0,
  '& path': {
    fill: 'currentColor',
  },
};

const ComposerActionsMenu = ({
  onAttachmentChange,
  disabled = false,
  canOpenAdvanced = false,
  canOpenDigitalSignature = false,
  ommnioSignInactiveMarketingUrl,
  canSendArticle = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const openAcknowledge = useActions(acknowledgeCreateDrawer.open);
  const openForm = useActions(formDrawer.open);
  const navigate = useNavigate();
  const match = useMatch('/home/messages/:id/*');
  const {
    values: { sentAsChannel },
  } = useFormikContext();

  const openMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenAcknowledge = () => {
    closeMenu();
    openAcknowledge({ sentAsChannel, digitalSignatureMode: false });
  };

  const handleOpenDigitalSignature = () => {
    closeMenu();
    openAcknowledge({ sentAsChannel, digitalSignatureMode: true });
  };

  const handleOpenForm = () => {
    closeMenu();
    openForm({ sentAsChannel });
  };

  const handleAttachmentChange = event => {
    onAttachmentChange(event);
    closeMenu();
  };

  const handleArticle = () => {
    closeMenu();
    if (!match?.pathnameBase) return;
    navigate(`${match.pathnameBase}/article/new`);
  };

  const dividerItemSx = theme => ({
    ...menuItemSx(theme),
    borderBottom: `1px solid ${theme.palette.divider}`,
  });

  const attachItemSx = theme => ({
    ...dividerItemSx(theme),
    position: 'relative',
    overflow: 'hidden',
  });

  return (
    <>
      <IconButton
        size="small"
        onClick={openMenu}
        disabled={disabled}
        aria-label={I18n.t('SendMessageForm.Open message actions')}
      >
        <AttachFileIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: theme => ({
              borderRadius: '6px',
              minWidth: 220,
              boxShadow: theme.shadows[6],
            }),
          },
          list: { sx: { py: 0 } },
        }}
      >
        <MenuItem component="label" disableRipple sx={attachItemSx}>
          <Box component="span" sx={iconSx}>
            <AttachFileIcon fontSize="small" />
          </Box>
          <Typography variant="body2">{I18n.t('SendMessageForm.Attach file')}</Typography>
          <FileInput inputRef={fileInputRef} onChange={handleAttachmentChange} multiple />
        </MenuItem>
        {canOpenAdvanced ? (
          <MenuItem onClick={handleOpenAcknowledge} sx={dividerItemSx}>
            <Box component="span" sx={iconSx}>
              <StarIcon fontSize="small" />
            </Box>
            <Typography variant="body2">{I18n.t('SendMessageForm.Acknowledge message')}</Typography>
          </MenuItem>
        ) : null}
        {canOpenDigitalSignature ? (
          ommnioSignInactiveMarketingUrl ? (
            <Tooltip
              title={
                <OmmnioSignInactiveTooltipContent marketingUrl={ommnioSignInactiveMarketingUrl} />
              }
              arrow
              placement="right"
              enterDelay={200}
              slotProps={richTooltipSlotProps}
            >
              <Box component="span" sx={{ display: 'block' }}>
                <MenuItem disabled sx={dividerItemSx} onClick={e => e.stopPropagation()}>
                  <Box component="span" sx={iconSx}>
                    <Box component={SignIcon} sx={signMenuIconSx} aria-hidden />
                  </Box>
                  <Typography variant="body2">
                    {I18n.t('SendMessageForm.Digital signature message')}
                  </Typography>
                </MenuItem>
              </Box>
            </Tooltip>
          ) : (
            <MenuItem onClick={handleOpenDigitalSignature} sx={dividerItemSx}>
              <Box component="span" sx={iconSx}>
                <Box component={SignIcon} sx={signMenuIconSx} aria-hidden />
              </Box>
              <Typography variant="body2">
                {I18n.t('SendMessageForm.Digital signature message')}
              </Typography>
            </MenuItem>
          )
        ) : null}
        {canOpenAdvanced ? (
          <MenuItem onClick={handleOpenForm} sx={dividerItemSx}>
            <Box component="span" sx={iconSx}>
              <BoltIcon fontSize="small" />
            </Box>
            <Typography variant="body2">{I18n.t('SendMessageForm.Send form')}</Typography>
          </MenuItem>
        ) : null}
        {canOpenAdvanced && canSendArticle ? (
          <MenuItem onClick={handleArticle} sx={menuItemSx}>
            <Box component="span" sx={iconSx}>
              <ArticleIcon fontSize="small" />
            </Box>
            <Typography variant="body2">{I18n.t('SendMessageForm.Article')}</Typography>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

ComposerActionsMenu.propTypes = {
  onAttachmentChange: func.isRequired,
  disabled: bool,
  canOpenAdvanced: bool,
  canOpenDigitalSignature: bool,
  ommnioSignInactiveMarketingUrl: string,
  canSendArticle: bool,
};

export default ComposerActionsMenu;
